import React, { useContext, useState } from 'react';
import { useLocation } from 'react-router';
import { KibIngressCard } from '@chewy/kib-cards-react';
import { KibButtonNew } from '@chewy/kib-controls-react';
import {
  KibCloseIcon,
  KibMoreHorizIcon,
  KibChevronLeftIcon,
  KibRemoveOutlineIcon,
} from '@chewy/kib-icons-react';
import classNames from 'classnames';
import { KibSheet } from '@chewy/kib-overlays-react';
import { useMutation, useQueryClient } from 'react-query';
import { getPet, getActivePets, updatePetProfile } from 'api/petApi';
import { PetInterface } from 'types/pet';
import ManagePetSupport from 'components/manage-pet/ManagePetSupport/ManagePetSupport';
import { getSharedCopy } from 'components/manage-pet/copy';
import { getApiPetProfileFromInterface } from 'components/manage-pet/utils/getApiPetProfileFromInterface';
import AnalyticsContext from '../../context/AnalyticsContext';
import { getPetAllPath, getPetEditPath, getPetProfilePath } from '../../util/config';
import { getPetPossessivePronouns } from '../../util/getPetPronouns';

import useLocationQuery from '../../hooks/useLocationQuery';
import {
  ApiPetProfile,
  ApiPetProfileStatusEnum,
} from '../../api/generated-client/PetProfile-Service-A.v1';
import FetchError from '../../types/http';
import { handleAuthError } from '../../util/auth';
import log from '../../util/log';
import './memorial-menu.scss';

export default function MemorialMenu({
  petProfile,
  openDetails,
  setOpenDetails,
  setRemovedMemorial,
}: {
  petProfile: PetInterface;
  openDetails: boolean;
  setOpenDetails: React.Dispatch<React.SetStateAction<boolean>>;
  setRemovedMemorial: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const pronoun = getPetPossessivePronouns(petProfile.gender).toLowerCase();
  const overflowSheetTitle = 'Memorial options';
  const removePetTitle = 'Remove Memorial';
  const removePetSubtitle = `Remove ${pronoun} information and this memorial from your account.`;

  const [showOverflowSheet, setShowOverflowSheet] = useState(false);

  const location = useLocation();
  const analytics = useContext(AnalyticsContext);
  let query = useLocationQuery();
  const queryClient = useQueryClient();
  const redirectUrl = query.get('redirectUrl');

  const handleCloseNavigation = () => {
    analytics.trackRainbow({
      name: 'pet profile memorial - clicked',
      category: 'pet-profile-memorial',
      action: 'click',
      label: 'pet_profile_memorial_close',
    });
    if (
      !redirectUrl ||
      decodeURIComponent(redirectUrl) === getPetEditPath(petProfile.id) ||
      decodeURIComponent(redirectUrl) === getPetProfilePath(petProfile.id)
    ) {
      window.location.href = getPetAllPath();
    } else {
      window.location.href = decodeURIComponent(redirectUrl);
    }
  };

  const openMenuOptions = () => {
    setShowOverflowSheet(true);
    analytics.trackRainbow({
      name: 'pet profile memorial - clicked',
      category: 'pet-profile-memorial',
      action: 'click',
      label: 'pet_profile_memorial_overflow',
    });
  };

  const memorialMenuClassNames = classNames('memorial-menu', {
    'memorial-menu__details': !!openDetails,
  });

  const closeMemorialClassNames = classNames({
    'memorial-menu__close': !openDetails,
    'memorial-menu__hide': !!openDetails,
  });

  const handleRemove = () => {
    setRemovedMemorial(true);
    updatePet.mutate({
      ...getApiPetProfileFromInterface(petProfile),
      status: ApiPetProfileStatusEnum.Deleted,
    });
    analytics.trackRainbow({
      name: 'pet profile memorial - clicked',
      category: 'pet-profile-memorial',
      action: 'click',
      label: 'pet_profile_memorial_options_remove',
    });
  };

  const handleViewMenuSheet = () => {
    analytics.trackRainbow({
      name: 'pet profile memorial - viewed',
      category: 'pet-profile-memorial',
      action: 'impression',
      label: 'pet_profile_memorial_options_impression',
    });
  };

  const handleCloseMenuSheet = () => {
    setShowOverflowSheet(false);
    analytics.trackRainbow({
      name: 'pet profile memorial - clicked',
      category: 'pet-profile-memorial',
      action: 'click',
      label: 'pet_profile_memorial_options_close',
    });
  };

  const handleClickSupport = () => {
    analytics.trackRainbow({
      name: 'pet profile memorial - clicked',
      category: 'pet-profile-memorial',
      action: 'click',
      label: 'pet_profile_memorial_options_cs',
    });
  };

  const { contactPrompt, contactLinkText } = getSharedCopy(petProfile);

  const invalidateQueries = () => {
    const invalidQueries = [
      queryClient.removeQueries(getActivePets.key),
      queryClient.removeQueries([getPet.key, petProfile.id]),
    ];
    return Promise.all(invalidQueries);
  };

  const updatePet = useMutation(
    (payload: ApiPetProfile) => updatePetProfile(Number(petProfile.id), payload),
    {
      onSuccess: () => {
        try {
          invalidateQueries();
        } catch (error) {
          log.error('Failed to invalidate cache on remove memorial:', error);
        }
        // TODO add analytics: https://chewyinc.atlassian.net/browse/PPF-1194
      },
      onError: (error: FetchError) => {
        log.error('Failed to Update Pet Profile:', `Status code: ${error.status}`, error);
        handleAuthError(error.status, location.pathname);
      },
    },
  );
  return (
    <>
      <div className={memorialMenuClassNames}>
        {!openDetails ? (
          <KibButtonNew
            aria-label="View options for pet's memorial"
            data-testid="memorial-options-buttons"
            size="small"
            emphasis="tertiary"
            theme="utility"
            icon={<KibMoreHorizIcon />}
            iconOnly
            autoFocus={false}
            onClick={openMenuOptions}
          />
        ) : (
          <KibButtonNew
            aria-label="Return to pet's memorial"
            data-testid="memorial-return-button"
            size="small"
            emphasis="tertiary"
            theme="utility"
            icon={<KibChevronLeftIcon />}
            iconOnly
            onClick={() => {
              analytics.trackRainbow({
                name: 'pet profile memorial - clicked',
                category: 'pet-profile-memorial',
                action: 'click',
                label: 'pet_profile_memorial_details_back',
              });
              setOpenDetails(false);
            }}
            className="memorial-menu__back-btn"
          />
        )}
        <KibButtonNew
          className={closeMemorialClassNames}
          data-testid="memorial-close"
          aria-label="close your pet's memorial"
          title="Close Pet's Memorial"
          onClick={handleCloseNavigation}
          size="small"
          emphasis="tertiary"
          theme="utility"
          icon={<KibCloseIcon />}
          iconOnly
        />
      </div>
      {showOverflowSheet && (
        <KibSheet
          open={showOverflowSheet}
          onClose={handleCloseMenuSheet}
          onOpen={handleViewMenuSheet}
          title={overflowSheetTitle}
          size="large"
          data-testid="memorial-overflow-sheet"
          className="memorial-overflow-sheet__container"
        >
          <div className="memorial-overflow-sheet">
            <KibIngressCard
              className="memorial-menu__remove"
              title={removePetTitle}
              subtitle={removePetSubtitle}
              icon={<KibRemoveOutlineIcon className="memorial-menu__remove--icon" />}
              onClick={() => {
                handleRemove();
              }}
            />
            <ManagePetSupport
              supportText={contactPrompt}
              supportLink={contactLinkText}
              onClick={handleClickSupport}
            />
          </div>
        </KibSheet>
      )}
    </>
  );
}
